import {
  Agreement,
  AgreementStatus,
  CollateralMethod,
  CollateralType,
  Currency,
  Data,
  RoundingRule,
  SettlementSystem,
  SettlementType,
  Side,
  TermType,
  Timestamp,
  availableCollateralMethods,
  availableCurrencies,
  availableRoundingRules,
  availableSettlementSystems,
  availableTermTypes,
  formatCollateralMethod,
  formatCollateralType,
  formatCurrency,
  formatProfitCenter,
  formatRoundingRule,
  formatSettlementSystem,
  formatSettlementType,
  formatTermType,
  getAgreementStatusColor,
  getAgreementStatusName,
  sides,
} from '@/connect';
import { alignCenter, alignLeft, alignRight, component, sortNormalizedString } from './utils';
import { ColDef } from 'ag-grid-enterprise';
import { h } from 'vue';
import { VBtn, VChip, VIcon } from 'vuetify/lib';
import {
  timestamp,
  timestampAdapter,
  userIdentifier,
} from '@/modules/common/components/ag-table/columns/common';
import { formatCompanyInfo, formatDate, formatDecimalPercentage } from '@/modules/agreements/utils';
import { useIsIncoming } from '@/modules/agreements/composables';
import Decimal from 'decimal.js';
import { useCompaniesStore } from '@/store/companies-store';
import * as cols from '@/modules/common/components/ag-table/columns/common';
import {
  decimalIndependentAmountRateAsPercentCollateralValueFormatter,
  decimalPercentageValueFormatter,
  decimalValueFormatter,
  enumValueFormatter,
} from '@/modules/common/components/ag-table/columns/value-formatters';
import {
  filterDate,
  filterDecimal,
  filterSelect,
  filterSelectMultiple,
  filterText,
} from './filter';

export function id(config?: { filter?: boolean }): ColDef<Data<Agreement>, string> {
  return {
    field: 'id',
    colId: 'id',
    headerName: 'Agreement Id',
    ...alignLeft(),
    ...(config?.filter && filterText({ trim: true, mode: 'contains' })),
  };
}

const statusChipAdapter = component<{
  status: AgreementStatus;
  isIncoming: boolean;
}>((props) => () => {
  const name = getAgreementStatusName(props.status, props.isIncoming);
  const color = getAgreementStatusColor(props.status, props.isIncoming);
  return h(
    VChip,
    {
      props: { color, 'x-small': true },
      style: 'width: 100%; display: flex; flex-direction: column;',
    },
    [name]
  );
});

export function status(config?: { filter?: boolean }): ColDef<Data<Agreement>, AgreementStatus> {
  return {
    field: 'status',
    colId: 'status',
    headerName: 'Status',
    ...enumValueFormatter(AgreementStatus),
    cellRendererSelector: (params) =>
      statusChipAdapter({ status: params.value!, isIncoming: useIsIncoming(params.data!).value }),
    resizable: false,
    pinned: 'left',
    ...alignCenter(),
    ...(config?.filter && {
      filterValueGetter: (params: { data?: Data<Agreement> | null }) => {
        const status = params.data?.status;
        if (!status) return '';
        const isIncoming = useIsIncoming(params.data!).value;
        return getAgreementStatusName(status, isIncoming) ?? '';
      },
      ...filterSelectMultiple({
        values: [
          { value: 'Pending my approval', text: 'Pending my approval' },
          { value: 'Pending CP approval', text: 'Pending CP approval' },
          { value: 'Active', text: 'Active' },
          { value: 'Rejected', text: 'Rejected' },
          { value: 'Closed', text: 'Closed' },
          { value: 'Inactive', text: 'Inactive' },
        ],
      }),
    }),
  } as ColDef<Data<Agreement>, AgreementStatus>;
}

export function side(config?: { filter?: boolean }): ColDef<Data<Agreement>, Side> {
  return {
    field: 'side',
    colId: 'side',
    headerName: 'Side',
    ...enumValueFormatter(Side),
    cellRendererSelector: (params) =>
      cols.formatSideAdapter({
        side: params.value === Side.BORROW ? 'BORROW' : 'LOAN',
      }),
    ...alignLeft(),
    ...(config?.filter &&
      filterSelect([
        { value: null, text: 'All' },
        ...[...sides.entries()].map(([value, text]) => ({ value, text })),
      ])),
  };
}

export function counterpartyId(config?: { filter?: boolean }): ColDef<Data<Agreement>, string> {
  return {
    field: 'counterpartyId',
    colId: 'counterpartyId',
    headerName: 'Counterparty',
    valueFormatter: (params) => {
      if (!params.value) {
        return '';
      }

      const company = useCompaniesStore().getCompany(params.value);
      return company === null ? '' : formatCompanyInfo(company);
    },
    filterValueGetter: (params) => {
      const counterpartyId = params.data?.counterpartyId;
      if (!counterpartyId) {
        return '';
      }

      const company = useCompaniesStore().getCompany(counterpartyId);
      return company === null ? '' : formatCompanyInfo(company);
    },
    ...(config?.filter && filterText({ trim: true, mode: 'contains' })),
  };
}

export function shortName(config = { filter: true }): ColDef<Data<Agreement>, string> {
  return {
    field: 'shortName',
    colId: 'shortName',
    headerName: 'Agreement Short Name',
    initialWidth: 200,
    ...(config?.filter && filterText({ trim: true, mode: 'contains' })),
    ...alignLeft(),
    comparator: sortNormalizedString,
  };
}

export function bookingAccount(config?: {
  filter?: boolean;
}): ColDef<Data<Agreement>, string | undefined> {
  return {
    field: 'bookingAccount',
    colId: 'bookingAccount',
    headerName: 'Booking Account',
    valueFormatter: (params) => params.value ?? '–',
    ...(config?.filter && filterText({ trim: true, mode: 'contains' })),
    ...alignLeft(),
  };
}

export function counterpartyBookingAccount(config?: {
  filter?: boolean;
}): ColDef<Data<Agreement>, string | undefined> {
  return {
    field: 'counterpartyBookingAccount',
    colId: 'counterpartyBookingAccount',
    headerName: 'Counterparty Booking Account',
    valueFormatter: (params) => params.value ?? '–',
    ...(config?.filter && filterText({ trim: true, mode: 'contains' })),
    ...alignLeft(),
  };
}

export function tradeType(config?: {
  filter?: boolean;
}): ColDef<Data<Agreement>, string | undefined> {
  return {
    field: 'tradeType',
    colId: 'tradeType',
    headerName: 'Trade Type',
    valueFormatter: (params) => params.value ?? '–',
    ...(config?.filter && filterText({ trim: true, mode: 'contains' })),
    ...alignLeft(),
  };
}

export function settlementType(config?: {
  filter?: boolean;
}): ColDef<Data<Agreement>, SettlementType> {
  return {
    field: 'settlementType',
    colId: 'settlementType',
    headerName: 'Settlement Type',
    valueFormatter: (params) => formatSettlementType(params.value!),
    ...alignLeft(),
    ...(config?.filter &&
      filterSelectMultiple({
        values: [SettlementType.NSCC, SettlementType.BILATERAL, SettlementType.OCC].map(
          (value) => ({
            value,
            text: formatSettlementType(value),
          })
        ),
      })),
  };
}

export function percentCollateral(config?: { filter?: boolean }): ColDef<Data<Agreement>, string> {
  return {
    field: 'collateralRate',
    colId: 'percentCollateral',
    headerName: 'Collateral Rate',
    ...decimalIndependentAmountRateAsPercentCollateralValueFormatter(),
    ...alignRight({ hasPostfix: true }),
    ...(config?.filter && filterDecimal({ allowNegative: false })),
  };
}

export function roundingRule(config?: { filter?: boolean }): ColDef<Data<Agreement>, RoundingRule> {
  return {
    field: 'roundingRule',
    colId: 'roundingRule',
    headerName: 'Price Rounding Rules',
    valueFormatter: (params) => formatRoundingRule(params.value!),
    ...alignLeft(),
    ...(config?.filter &&
      filterSelectMultiple({
        values: availableRoundingRules.map(({ id, name }) => ({ value: id, text: name })),
      })),
  };
}

export function createdBy(config?: { filter?: boolean }): ColDef<Data<Agreement>, string> {
  return {
    ...userIdentifier(),
    field: 'proposerUser',
    colId: 'proposerUser.email',
    headerName: 'Created By',
    ...(config?.filter && filterText({ trim: true, mode: 'contains' })),
  };
}

export function approvedBy(config?: {
  filter?: boolean;
}): ColDef<Data<Agreement>, string | undefined> {
  return {
    ...userIdentifier(),
    field: 'responderUser',
    colId: 'responderUser.email',
    headerName: 'Approved By',
    ...(config?.filter && filterText({ trim: true, mode: 'contains' })),
  };
}

export function createdAt(config?: { filter?: boolean }): ColDef<Data<Agreement>, Timestamp> {
  return timestamp({
    field: 'createdAt',
    colId: 'createdAt',
    headerName: 'Created At',
    filter: config?.filter ? 'datetime' : false,
  });
}

export function approvedAt(config?: {
  filter?: boolean;
}): ColDef<Data<Agreement>, Timestamp | undefined> {
  return timestamp({
    field: 'approvedAt',
    colId: 'approvedAt',
    headerName: 'Approved At',
    filter: config?.filter ? 'datetime' : false,
  });
}

export function collateralType(config?: {
  filter?: boolean;
}): ColDef<Data<Agreement>, CollateralType> {
  return {
    field: 'collateralType',
    colId: 'collateralType',
    headerName: 'Collateral Type',
    valueFormatter: (params) => formatCollateralType(params.value!),
    ...alignLeft(),
    ...(config?.filter &&
      filterSelectMultiple({
        values: [
          CollateralType.CASH,
          CollateralType.NON_CASH_EQ,
          CollateralType.NON_CASH_JGB,
          CollateralType.NON_CASH_UST,
        ].map((value) => ({
          value,
          text: formatCollateralType(value),
        })),
      })),
  };
}

export function pricingCurrency(config?: { filter?: boolean }): ColDef<Data<Agreement>, Currency> {
  return {
    field: 'pricingCurrency',
    colId: 'pricingCurrency',
    headerName: 'Pricing Currency',
    valueFormatter: (params) => formatCurrency(params.value!),
    ...alignLeft(),
    ...(config?.filter &&
      filterSelectMultiple({
        values: availableCurrencies.map(({ id, name }) => ({ value: id, text: name })),
      })),
  };
}

export function billingCurrency(config?: { filter?: boolean }): ColDef<Data<Agreement>, Currency> {
  return {
    field: 'billingCurrency',
    colId: 'billingCurrency',
    headerName: 'Billing Currency',
    valueFormatter: (params) => formatCurrency(params.value!),
    ...alignLeft(),
    ...(config?.filter &&
      filterSelectMultiple({
        values: availableCurrencies.map(({ id, name }) => ({ value: id, text: name })),
      })),
  };
}

export function collateralCurrency(config?: {
  filter?: boolean;
}): ColDef<Data<Agreement>, Currency | undefined> {
  return {
    field: 'collateralCurrency',
    colId: 'collateralCurrency',
    headerName: 'Collateral Currency',
    valueFormatter: (params) => formatCurrency(params.value!),
    ...alignLeft(),
    ...(config?.filter &&
      filterSelectMultiple<Data<Agreement>, Currency | undefined>({
        values: availableCurrencies.map(({ id, name }) => ({ value: id, text: name })),
      })),
  };
}

export function collateralMethod(config?: {
  filter?: boolean;
}): ColDef<Data<Agreement>, CollateralMethod> {
  return {
    field: 'collateralMethod',
    colId: 'collateralMethod',
    headerName: 'Collateral Method',
    valueFormatter: (params) => formatCollateralMethod(params.value!),
    ...alignLeft(),
    ...(config?.filter &&
      filterSelectMultiple({
        values: availableCollateralMethods.map(({ id, name }) => ({ value: id, text: name })),
      })),
  };
}

export function dividendRequired(config?: {
  filter?: boolean;
}): ColDef<Data<Agreement>, string | undefined> {
  return {
    field: 'dividendRequired',
    colId: 'dividendRequired',
    headerName: 'Dividend % to Lender',
    valueFormatter: (params) => {
      if (!params.value) return '–\xA0\xA0'; // postfix padding
      return formatDecimalPercentage(new Decimal(params.value));
    },
    ...alignRight({ hasPostfix: true }),
    ...(config?.filter && filterDecimal({ allowNegative: false })),
  };
}

export function termType(config?: { filter?: boolean }): ColDef<Data<Agreement>, TermType> {
  return {
    field: 'termType',
    colId: 'termType',
    headerName: 'Term Type',
    valueFormatter: (params) => formatTermType(params.value!),
    ...alignLeft(),
    ...(config?.filter &&
      filterSelectMultiple({
        values: availableTermTypes.map(({ id, name }) => ({ value: id, text: name })),
      })),
  };
}

export function termStartDate(config?: {
  filter?: boolean;
}): ColDef<Data<Agreement>, string | undefined> {
  return {
    field: 'termStartDate',
    colId: 'termStartDate',
    headerName: 'Term Start Date',
    cellRendererSelector: (params) => timestampAdapter(formatDate(params.value)),
    ...alignLeft(),
    ...(config?.filter && filterDate()),
  };
}

export function termEndDate(config?: {
  filter?: boolean;
}): ColDef<Data<Agreement>, string | undefined> {
  return {
    field: 'termEndDate',
    colId: 'termEndDate',
    headerName: 'Term End Date',
    cellRendererSelector: (params) => timestampAdapter(formatDate(params.value)),
    ...alignLeft(),
    ...(config?.filter && filterDate()),
  };
}

export function balanceTarget(config?: {
  filter?: boolean;
}): ColDef<Data<Agreement>, string | undefined> {
  return {
    field: 'balanceTarget',
    colId: 'balanceTarget',
    headerName: 'Balance Target',
    ...decimalValueFormatter(),
    ...alignRight(),
    ...(config?.filter && filterDecimal({ allowNegative: false })),
  };
}

export function balanceTargetVariance(config?: {
  filter?: boolean;
}): ColDef<Data<Agreement>, string | undefined> {
  return {
    field: 'balanceTargetVariance',
    colId: 'balanceTargetVariance',
    headerName: 'Balance Target Variance',
    ...decimalPercentageValueFormatter(),
    ...alignRight({ hasPostfix: true }),
    ...(config?.filter && filterDecimal({ allowNegative: false })),
  };
}

export function settlementSystem(config?: {
  filter?: boolean;
}): ColDef<Data<Agreement>, SettlementSystem | undefined> {
  return {
    field: 'settlementSystem',
    colId: 'settlementSystem',
    headerName: 'Settlement System',
    valueFormatter: (params) => formatSettlementSystem(params.value!),
    ...alignLeft(),
    ...(config?.filter &&
      filterSelectMultiple<Data<Agreement>, SettlementSystem | undefined>({
        values: availableSettlementSystems.map(({ id, name }) => ({ value: id, text: name })),
      })),
  };
}

export function profitCenter(config: {
  hiddenByDefault: boolean;
  filter?: boolean;
}): ColDef<Data<Agreement>, string | undefined> {
  return {
    field: 'profitCenter',
    colId: 'profitCenter',
    headerName: 'Profit Center',
    valueFormatter: (params) => formatProfitCenter(params.value ?? undefined),
    initialHide: config.hiddenByDefault,
    ...alignLeft(),
    ...(config?.filter && filterText({ trim: true, mode: 'contains' })),
  };
}

const actionsAdapter = component<{
  item: Agreement;
  columns: number;
  getActionCallbacks: (agreement: Agreement) => {
    view?: () => void;
    duplicate?: () => void;
    edit?: () => void;
    close?: () => void;
    review?: () => void;
  };
}>((props) => () => {
  const { view, duplicate, edit, close, review } = props.getActionCallbacks(props.item);
  const shouldUseGridAreas = props.columns === 4;
  return h(
    'div',
    {
      class: 'actions',
      style: `gap: 0.5rem; display: grid; ${!shouldUseGridAreas ? '' : 'grid-template-areas: "view duplicate edit close";'} grid-template-columns: repeat(${props.columns}, 1fr);`,
    },
    [
      view &&
        h(
          VBtn,
          {
            class: 'icon-action',
            style: !shouldUseGridAreas ? '' : 'grid-area: view;',
            attrs: {
              'data-test': 'agreement-view',
              icon: true,
              'x-small': true,
              title: 'View',
            },
            on: { click: view },
          },
          [h(VIcon, {}, 'mdi-eye')]
        ),
      duplicate &&
        h(
          VBtn,
          {
            class: 'icon-action',
            style: !shouldUseGridAreas ? '' : 'grid-area: duplicate;',
            attrs: {
              'data-test': 'agreement-duplicate',
              icon: true,
              'x-small': true,
              title: 'Duplicate',
            },
            on: { click: duplicate },
          },
          [h(VIcon, {}, 'mdi-plus-circle-multiple-outline')]
        ),
      edit &&
        h(
          VBtn,
          {
            class: 'icon-action',
            style: !shouldUseGridAreas ? '' : 'grid-area: edit;',
            attrs: {
              'data-test': 'agreement-edit',
              icon: true,
              'x-small': true,
              title: 'Edit',
            },
            on: { click: edit },
          },
          [h(VIcon, {}, 'mdi-pencil')]
        ),
      close &&
        h(
          VBtn,
          {
            class: 'icon-action',
            style: !shouldUseGridAreas ? '' : 'grid-area: close;',
            attrs: {
              'data-test': 'agreement-close',
              icon: true,
              'x-small': true,
              title: 'Close',
            },
            on: { click: close },
          },
          [h(VIcon, {}, 'mdi-close')]
        ),
      review &&
        h(
          VBtn,
          {
            class: 'icon-action',
            style: !shouldUseGridAreas ? '' : 'grid-column: 2 / span 2;',
            attrs: {
              'data-test': 'agreement-review',
              icon: true,
              'x-small': true,
              title: 'Review',
            },
            on: { click: review },
          },
          [h(VIcon, {}, 'mdi-check')]
        ),
    ]
  );
});

export function actions(
  columns: number,
  getActionCallbacks: (agreement: Agreement) => {
    view?: () => void;
    duplicate?: () => void;
    edit?: () => void;
    close?: () => void;
    review?: () => void;
  }
): ColDef<Data<Agreement>> {
  return {
    colId: 'actions',
    headerName: 'Actions',
    cellRendererSelector: (params) =>
      actionsAdapter({
        item: params.data as Agreement,
        columns,
        getActionCallbacks,
      }),
    pinned: 'right',
    lockVisible: true,
    suppressColumnsToolPanel: true,
    ...alignCenter(),
  };
}
